/**
 * CompileNowButton Component
 * 
 * Shows when rotation is not compiled yet
 * Triggers compilation and refreshes page when done
 */

import React, { useState } from 'react';
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

const CompileNowButton = ({ rotationNumber, employeeId, onCompiled }) => {
  const [isCompiling, setIsCompiling] = useState(false);
  const [error, setError] = useState(null);
  
  const handleCompile = async () => {
    setIsCompiling(true);
    setError(null);
    
    try {
      // Trigger compilation
      await axios.post(
        `${API_BASE_URL}/api/v1/rotations/${rotationNumber}/recompile`,
        {},
        { params: { employee_id: employeeId } }
      );
      
      // Wait a moment for compilation to finish
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Notify parent to refresh
      onCompiled();
      
    } catch (err) {
      console.error('Compilation failed:', err);
      setError(err.response?.data?.detail || 'Failed to compile rotation');
    } finally {
      setIsCompiling(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        <div className="mb-6">
          <div className="mx-auto w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center">
            <span className="text-3xl">⚠️</span>
          </div>
        </div>
        
        <h2 className="text-2xl font-bold text-gray-900 mb-3">
          Rotation Not Compiled
        </h2>
        
        <p className="text-gray-600 mb-6">
          This rotation hasn't been compiled yet. Compilation calculates duty times, 
          credit hours, and FAR 117 compliance.
        </p>
        
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
            {error}
          </div>
        )}
        
        <button
          onClick={handleCompile}
          disabled={isCompiling}
          className={`
            w-full px-6 py-3 rounded-lg font-semibold text-white
            transition-all
            ${isCompiling
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700 active:scale-95'
            }
          `}
        >
          {isCompiling ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Compiling...
            </span>
          ) : (
            'Compile Now'
          )}
        </button>
        
        <p className="text-xs text-gray-500 mt-4">
          This usually takes 2-3 seconds
        </p>
      </div>
    </div>
  );
};

export default CompileNowButton;
